// Cart variant reconciliation.
// Enforces the two-variant invariant set up by the MJ Subscription block:
//   - If the cart contains ANY subscription line (selling_plan_allocation set),
//     all bag lines must be Variant B (the discounted/subscriber rate).
//   - If the cart has NO subscription line, all bag lines must be Variant A
//     (the standard / full price rate).
//
// Bag lines are identified by the `_mj_bag_line` line item property. Each bag
// line carries `_mj_variant_a` and `_mj_variant_b` properties pointing at the
// two variants it can swap between, so this module never needs hardcoded IDs.
//
// Triggered on cart:update events (which the theme dispatches whenever the
// cart changes). Swaps variants by removing the offending line and re-adding
// at the correct variant. Re-fires cart:update after the swap so the drawer
// re-renders.

import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

const RECONCILE_FLAG = '__mjReconcileInFlight';
const SOURCE_TAG = 'mj-cart-reconcile';

// Read /cart.js and return the parsed JSON.
async function fetchCart() {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  return response.json();
}

// Determine whether a swap is needed and which lines need to change.
// Returns an array of { line, targetVariantId, sellingPlan, properties }.
function planSwap(cart) {
  const bagLines = cart.items.filter(
    (item) => item.properties && item.properties._mj_bag_line
  );
  if (bagLines.length === 0) return [];

  const hasSubscription = bagLines.some(
    (item) => item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
  );

  const swaps = [];
  for (const line of bagLines) {
    const variantA = parseInt(line.properties._mj_variant_a || '0', 10);
    const variantB = parseInt(line.properties._mj_variant_b || '0', 10);
    if (!variantA || !variantB || variantA === variantB) continue;

    const desired = hasSubscription ? variantB : variantA;
    if (line.variant_id === desired) continue;

    swaps.push({
      lineKey: line.key,
      currentVariant: line.variant_id,
      targetVariant: desired,
      quantity: line.quantity,
      sellingPlan:
        line.selling_plan_allocation && line.selling_plan_allocation.selling_plan
          ? line.selling_plan_allocation.selling_plan.id
          : null,
      properties: { ...line.properties },
    });
  }

  return swaps;
}

// Apply swaps sequentially. /cart/change.js doesn't support variant swaps,
// so we remove the line (qty=0) and add a new one with the target variant.
async function applySwaps(swaps, sectionId) {
  for (const swap of swaps) {
    // Remove the existing line.
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: swap.lineKey, quantity: 0 }),
    });

    // Add the new line with the same qty, properties, and (optionally) plan.
    const addBody = {
      items: [
        {
          id: swap.targetVariant,
          quantity: swap.quantity,
          properties: swap.properties,
          ...(swap.sellingPlan ? { selling_plan: swap.sellingPlan } : {}),
        },
      ],
    };
    if (sectionId) {
      addBody.sections = sectionId;
      addBody.sections_url = location.pathname;
    }
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(addBody),
    });
  }
}

async function reconcile() {
  // Guard against re-entry — our own swaps trigger cart:update events.
  if (window[RECONCILE_FLAG]) return;
  window[RECONCILE_FLAG] = true;

  try {
    const cart = await fetchCart();
    const swaps = planSwap(cart);
    if (swaps.length === 0) return;

    const cartItemsComp = document.querySelector('cart-items-component[data-section-id]');
    const sectionId = cartItemsComp?.dataset.sectionId;

    await applySwaps(swaps, sectionId);

    // Pull the updated cart and re-broadcast so the drawer/section morph.
    const updated = await fetchCart();

    if (sectionId) {
      // Fetch a section render so cart-items can morph into the latest HTML.
      const sectionResponse = await fetch(
        `/cart?sections=${encodeURIComponent(sectionId)}&sections_url=${encodeURIComponent(location.pathname)}`,
        { headers: { Accept: 'application/json' } }
      ).catch(() => null);
      let sectionsHtml = null;
      if (sectionResponse && sectionResponse.ok) {
        const sectionsJson = await sectionResponse.json();
        sectionsHtml = sectionsJson?.[sectionId];
      }
      if (sectionsHtml) {
        morphSection(sectionId, sectionsHtml, { mode: 'hydration' });
      }
    }

    document.dispatchEvent(
      new CartUpdateEvent(updated, SOURCE_TAG, {
        source: SOURCE_TAG,
        itemCount: updated.item_count,
      })
    );
  } catch (err) {
    console.error('[mj-cart-reconcile] failed', err);
  } finally {
    window[RECONCILE_FLAG] = false;
  }
}

// Bundle delete: a single click on the bundle's "remove" button removes ALL
// cart lines that share the same `_mj_bag_line` property — atomic delete so
// the customer never ends up with a partial bundle.
async function handleBundleDelete(event) {
  const btn = event.target.closest('[data-mj-bundle-delete]');
  if (!btn) return;
  const row = btn.closest('[data-mj-bundle-id]');
  const bundleId = row?.dataset.mjBundleId;
  if (!bundleId) {
    console.warn('[mj-cart-reconcile] bundle delete clicked but no bundle id found', btn);
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  // Don't gate on RECONCILE_FLAG — bundle delete should run independently of
  // the variant-swap reconciler, which is fine because they target different
  // line identities.
  btn.disabled = true;
  console.info('[mj-cart-reconcile] removing bundle', bundleId);

  try {
    const cart = await fetchCart();
    const lineKeys = cart.items
      .filter((item) => item.properties && item.properties._mj_bag_line === bundleId)
      .map((item) => item.key);
    if (lineKeys.length === 0) return;

    // Single atomic request — /cart/update.js with multiple keys at qty 0.
    // Avoids race conditions where line keys shift between sequential
    // /cart/change.js calls.
    const updates = {};
    for (const key of lineKeys) {
      updates[key] = 0;
    }
    const updateResponse = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    const updated = await updateResponse.json();
    const cartItemsComp = document.querySelector('cart-items-component[data-section-id]');
    const sectionId = cartItemsComp?.dataset.sectionId;

    document.dispatchEvent(
      new CartUpdateEvent(updated, SOURCE_TAG, {
        source: SOURCE_TAG,
        itemCount: updated.item_count,
      })
    );

    if (sectionId) {
      const sectionResponse = await fetch(
        `/cart?sections=${encodeURIComponent(sectionId)}&sections_url=${encodeURIComponent(location.pathname)}`,
        { headers: { Accept: 'application/json' } }
      ).catch(() => null);
      if (sectionResponse && sectionResponse.ok) {
        const sectionsJson = await sectionResponse.json();
        const sectionsHtml = sectionsJson?.[sectionId];
        if (sectionsHtml) {
          morphSection(sectionId, sectionsHtml, { mode: 'hydration' });
        }
      }
    }
  } catch (err) {
    console.error('[mj-cart-reconcile] bundle delete failed', err);
  } finally {
    btn.disabled = false;
  }
}

if (!window.__mjCartReconcileInit) {
  window.__mjCartReconcileInit = true;
  // Variant-swap reconciliation is currently DISABLED. It interacts badly
  // with Shopify's `allocation_method: "each"` Buy-X-Get-Y discounts —
  // /cart/change.js qty:0 doesn't fully remove units that are locked to a
  // discount allocation, leading to phantom items piling up after each ATC.
  // We don't need it for normal customer flows because:
  //   - The two-variant ATC always adds the correct variant from the start.
  //   - The cart UI hides per-line quantity controls, so customers can't
  //     manually move lines into an inconsistent state.
  //   - Bundle delete (below) atomically removes all lines via /cart/update.js
  //     in a single request, which correctly handles discount-locked lines.
  // Re-enable only if a flow emerges that produces mixed-variant bag lines.
  //
  // document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  //   if (event?.detail?.data?.source === SOURCE_TAG) return;
  //   reconcile();
  // });

  document.addEventListener('click', handleBundleDelete);
}
